import { DevelopmentError } from '../../webmodule-common/other/development-error';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { noParalellExecutionAsync } from '../../webmodule-common/other/common/helpers/callbacks';
import { QuoteSupplier, V6OfflineCause } from '../quotes/quote-service';
import { LangSupplierServices } from '../../localization/scripts/v6config/supplier-services-lang';

interface GSNCache {
  supplierId: string;
  gsn: GlobalSupplierNotification;
}

export class GlobalSupplierNotification {
  public static displaySupplierOffLineMsg = noParalellExecutionAsync(displaySupplierOffLineMsg);
  public static displaySupplierUpgradingMsg = noParalellExecutionAsync(displaySupplierOffUpgrade);
  private static _instances: GSNCache[] = [];
  supplierId: any;
  public extraText: string = '';
  private _events: ((gsn: GlobalSupplierNotification) => void)[] = [];
  private _quoteSupplier?: QuoteSupplier;
  private _date?: Date;

  constructor(supplierId) {
    this.supplierId = supplierId;
  }

  private _online?: boolean;

  public get online(): boolean {
    return this._online ?? false;
  }

  private _offlineCause?: V6OfflineCause | undefined;

  public get offlineCause(): V6OfflineCause | undefined {
    return this._offlineCause;
  }

  public get offlineMessage(): string {
    const supplierName = this._quoteSupplier?.description ?? LangSupplierServices.supplier();
    switch (this._offlineCause) {
      case V6OfflineCause.Connection:
        return LangSupplierServices.supplierOfflineSince({
          supplierName,
          time: this._date?.toLocaleTimeString() ?? '',
          extraText: this.extraText
        });
      case V6OfflineCause.Upgrading:
        return LangSupplierServices.supplierOfflineUpgradingSince({
          supplierName,
          time: this._date?.toLocaleTimeString() ?? '',
          extraText: this.extraText
        });
      case V6OfflineCause.VersionMismatch:
        return LangSupplierServices.supplierOfflineVersionMismatch({ supplierName });
      default:
        return LangSupplierServices.supplierOfflineUnknownReasonSince({
          supplierName,
          time: this._date?.toLocaleTimeString() ?? '',
          extraText: this.extraText
        });
    }
  }

  public static getDefaultInstance(): GlobalSupplierNotification | undefined {
    if (this._instances.length > 0) return this._instances[0].gsn;
    return undefined;
  }

  public static getInstance(supplierId?: string): GlobalSupplierNotification {
    if (!supplierId) {
      if (this._instances.length > 0) return this._instances[0].gsn;
      throw new DevelopmentError('GlobalSupplierNotification missing default supplier');
    }
    let instance = this._instances.find(x => x.supplierId === supplierId);
    if (!instance) {
      instance = { supplierId: supplierId, gsn: new GlobalSupplierNotification(supplierId) };
      this._instances.push(instance);
    }
    return instance.gsn;
  }

  public static async showOfflineMessage(offlineCause?: V6OfflineCause, offlineMessage?: string) {
    if (offlineMessage) console.log(`GlobalSupplierNotification: ${offlineMessage}`);
    if (offlineCause === V6OfflineCause.Upgrading) await GlobalSupplierNotification.displaySupplierUpgradingMsg();
    else await GlobalSupplierNotification.displaySupplierOffLineMsg();
  }

  public addEventListenter(event: (gsn: GlobalSupplierNotification) => void) {
    this._events = [...this._events.filter(x => x !== event), event];
  }

  public remEventListenter(event: (gsn: GlobalSupplierNotification) => void) {
    this._events = this._events.filter(x => x !== event);
  }

  public async makeOffline(offlineCause: V6OfflineCause, offlineMessage?: string) {
    this._offlineCause = offlineCause;
    const wasOnline = this._online;
    this._online = false;
    if (wasOnline) await this.showOfflineMessage(offlineMessage);
    this._events.forEach(x => x(this));
  }

  async showOfflineMessage(offlineMessage?: string) {
    if (offlineMessage) console.log(`GlobalSupplierNotification: ${offlineMessage}`);

    if (this._offlineCause === V6OfflineCause.Upgrading) await GlobalSupplierNotification.displaySupplierUpgradingMsg();
    else await GlobalSupplierNotification.displaySupplierOffLineMsg();
  }

  public setState(quotesupplierInfo: QuoteSupplier) {
    if (
      this._online !== quotesupplierInfo.online ||
      (!this._online && this._offlineCause !== quotesupplierInfo.offlineCause)
    ) {
      this._offlineCause = quotesupplierInfo.offlineCause;
      this._online = quotesupplierInfo.online;
      this._quoteSupplier = quotesupplierInfo;
      this._date = new Date();

      this._events.forEach(x => x(this));
    }
  }
}

async function displaySupplierOffUpgrade() {
  await information(LangSupplierServices.supplierOfflineUpgradingMsg(), LangSupplierServices.supplierOfflineTitle());
}

async function displaySupplierOffLineMsg() {
  await information(LangSupplierServices.supplierOfflineMsg(), LangSupplierServices.supplierOfflineTitle());
}

export async function displayFrameConfigLoadErrorMsg() {
  await information(LangSupplierServices.supplierFrameConfigOfflineMsg(), LangSupplierServices.supplierOfflineTitle());
}

let _displaySupplierTACNotApprovedMsg = false;

export async function displaySupplierTACNotApprovedMsg(supplier: QuoteSupplier, force?: boolean) {
  if (!force && _displaySupplierTACNotApprovedMsg) return;
  _displaySupplierTACNotApprovedMsg = true;
  await information(LangSupplierServices.tacApprovalNotSetMsg({ supplier: supplier.description }));
  // eslint-disable-next-line consistent-return
  return false;
}
